/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Team Members, Invitations & Workspace RBAC Store (Task 10.1.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export type WorkspaceMemberRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface WorkspaceMember {
  id: string;
  workspaceId: string;
  userId: string;
  fullName: string;
  email: string;
  avatarUrl?: string | null;
  role: WorkspaceMemberRole;
  status: 'active' | 'suspended';
  joinedAt: string;
}

export interface WorkspaceInvitation {
  id: string;
  workspaceId: string;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  token: string;
  invitedBy: string;
  expiresAt: string;
  createdAt: string;
}

export interface WorkspaceSubscription {
  planId: string;
  planName: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  currentPeriodEnd: string;
  seatsUsed: number;
  seatsLimit: number;
  features: string[];
}

interface TeamState {
  members: WorkspaceMember[];
  invitations: WorkspaceInvitation[];
  subscription: WorkspaceSubscription | null;
  isLoading: boolean;

  setMembers: (members: WorkspaceMember[]) => void;
  addMember: (member: WorkspaceMember) => void;
  updateMemberRole: (memberId: string, role: 'admin' | 'member' | 'viewer') => boolean;
  removeMember: (memberId: string) => boolean;
  createInvitation: (
    workspaceId: string,
    email: string,
    role: 'admin' | 'member' | 'viewer',
    invitedBy: string,
  ) => WorkspaceInvitation;
  cancelInvitation: (invitationId: string) => void;
  setSubscription: (sub: WorkspaceSubscription) => void;
  setLoading: (loading: boolean) => void;
  resetTeamState: () => void;
}

const INITIAL_MEMBERS: WorkspaceMember[] = [
  {
    id: 'mem_01',
    workspaceId: 'ws_personal_001',
    userId: 'usr_admin_001',
    fullName: 'Le Viet Quang',
    email: 'quangle@fourelementscorp.com',
    avatarUrl: null,
    role: 'owner',
    status: 'active',
    joinedAt: '2026-01-15',
  },
  {
    id: 'mem_02',
    workspaceId: 'ws_personal_001',
    userId: 'usr_dev_002',
    fullName: 'AI Studio Engineer',
    email: 'engineer@example.com',
    avatarUrl: null,
    role: 'admin',
    status: 'active',
    joinedAt: '2026-02-01',
  },
  {
    id: 'mem_03',
    workspaceId: 'ws_personal_001',
    userId: 'usr_copywriter_003',
    fullName: 'Content Creator',
    email: 'writer@fourelementscorp.com',
    avatarUrl: null,
    role: 'member',
    status: 'active',
    joinedAt: '2026-03-10',
  },
];

const INITIAL_INVITATIONS: WorkspaceInvitation[] = [
  {
    id: 'inv_01',
    workspaceId: 'ws_personal_001',
    email: 'translator@partner.com',
    role: 'member',
    token: 'inv_tok_9918237198273',
    invitedBy: 'quangle@fourelementscorp.com',
    expiresAt: '2026-11-01',
    createdAt: '2026-10-24',
  },
];

const INITIAL_SUBSCRIPTION: WorkspaceSubscription = {
  planId: 'plan_studio_pro',
  planName: 'Studio Enterprise Pro',
  status: 'active',
  currentPeriodEnd: '2026-11-24',
  seatsUsed: 3,
  seatsLimit: 10,
  features: [
    '200,000 Monthly AI Credits',
    'Full Multi-Voice Studio & CAT Engine',
    'Vectorize Knowledge Base (Unlimited Chunks)',
    '10 Team Member Seats included',
    'Custom CDN Zero-Egress Domain',
  ],
};

export const useTeamStore = create<TeamState>((set, get) => ({
  members: INITIAL_MEMBERS,
  invitations: INITIAL_INVITATIONS,
  subscription: INITIAL_SUBSCRIPTION,
  isLoading: false,

  setMembers: (members) => set({ members }),

  addMember: (member) =>
    set((state) => ({ members: [...state.members, member] })),

  updateMemberRole: (memberId, role) => {
    const { members } = get();
    const target = members.find((m) => m.id === memberId);
    if (!target) return false;
    // Owners cannot have their role downgraded via member list
    if (target.role === 'owner') return false;

    set({
      members: members.map((m) => (m.id === memberId ? { ...m, role } : m)),
    });
    return true;
  },

  removeMember: (memberId) => {
    const { members } = get();
    const target = members.find((m) => m.id === memberId);
    if (!target) return false;
    // Cannot delete the workspace owner
    if (target.role === 'owner') return false;

    set({
      members: members.filter((m) => m.id !== memberId),
    });
    return true;
  },

  createInvitation: (workspaceId, email, role, invitedBy) => {
    const newInvitation: WorkspaceInvitation = {
      id: `inv_${Date.now()}`,
      workspaceId,
      email: email.trim().toLowerCase(),
      role,
      token: `inv_tok_${Math.random().toString(36).substring(2)}${Date.now()}`,
      invitedBy,
      expiresAt: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
      createdAt: new Date().toISOString().split('T')[0],
    };

    set((state) => ({
      invitations: [...state.invitations, newInvitation],
    }));

    return newInvitation;
  },

  cancelInvitation: (invitationId) =>
    set((state) => ({
      invitations: state.invitations.filter((inv) => inv.id !== invitationId),
    })),

  setSubscription: (subscription) => set({ subscription }),

  setLoading: (isLoading) => set({ isLoading }),

  resetTeamState: () =>
    set({
      members: INITIAL_MEMBERS,
      invitations: INITIAL_INVITATIONS,
      subscription: INITIAL_SUBSCRIPTION,
      isLoading: false,
    }),
}));
